/**
 * Authentication Service
 * Handles login, logout, and user management
 */

import api from './api';

class AuthService {
  /**
   * Login user
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} User data and tokens
   */
  async login(email, password) {
    // Backend expects 'login' field which accepts email or username
    const response = await api.post('/auth/login', { login: email, password }, { includeAuth: false });

    if (response.success && response.data) {
      // Store tokens - handle both structures
      const tokens = response.data.tokens || response.data;
      const accessToken = tokens.accessToken || response.data.accessToken;
      const refreshToken = tokens.refreshToken || response.data.refreshToken;

      if (accessToken) {
        api.setToken(accessToken);
      }
      if (refreshToken) {
        api.setRefreshToken(refreshToken);
      }

      // Store user data
      if (response.data.user) {
        api.setUser(response.data.user);
      }

      return {
        user: response.data.user,
        accessToken,
        refreshToken,
      };
    }

    throw new Error(response.message || 'Login failed');
  }

  /**
   * Logout user
   */
  async logout() {
    try {
      // Only call logout endpoint if user has a token
      const token = api.getToken();
      if (token) {
        await api.post('/auth/logout');
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local data
      api.removeTokens();
    }
  }

  /**
   * Get current user from server
   * @returns {Promise<Object>} Current user data
   */
  async getCurrentUser() {
    const response = await api.get('/auth/me');

    if (response.success && response.data) {
      const user = response.data.user || response.data;
      api.setUser(user);
      return user;
    }

    throw new Error('Failed to get current user');
  }

  /**
   * Get stored user data from localStorage
   * @returns {Object|null}
   */
  getStoredUser() {
    return api.getUser();
  }

  /**
   * Alias for getStoredUser (for compatibility)
   */
  getUser() {
    return api.getUser();
  }

  /**
   * Send forgot password email
   * @param {string} email - User email
   * @returns {Promise<boolean>}
   */
  async forgotPassword(email) {
    const response = await api.post('/auth/forgot-password', { email }, { includeAuth: false });

    if (response.success) {
      return true;
    }

    throw new Error(response.message || 'Failed to send reset email');
  }

  /**
   * Check if user is authenticated (has token)
   * @returns {boolean}
   */
  isAuthenticated() {
    return !!api.getToken();
  }

  /**
   * Check if user has specific role
   * @param {string[]} requiredRoles - Array of role names
   * @returns {boolean}
   */
  hasRole(requiredRoles) {
    const user = this.getStoredUser();
    if (!user || !user.role) return false;

    const roleName = user.role.name || user.role;
    return requiredRoles.includes(roleName);
  }

  /**
   * Check if user has specific permission
   * @param {string} permission - Permission name
   * @returns {boolean}
   */
  hasPermission(permission) {
    const user = this.getStoredUser();
    if (!user || !user.role || !user.role.permissions) return false;

    return user.role.permissions.includes(permission);
  }

  /**
   * Check if user is admin
   * @returns {boolean}
   */
  isAdmin() {
    return this.hasRole(['Admin', 'Super Admin', 'ADMIN', 'SUPER_ADMIN']);
  }

  /**
   * Update user profile
   * @param {Object} data - Profile data
   * @returns {Promise<Object>}
   */
  async updateProfile(data) {
    const response = await api.put('/auth/profile', data);

    if (response.success && response.data) {
      const user = response.data.user || response.data;
      // Update stored user
      const currentUser = api.getUser();
      api.setUser({ ...currentUser, ...user });
      return user;
    }

    throw new Error('Failed to update profile');
  }

  /**
   * Change password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<boolean>}
   */
  async changePassword(currentPassword, newPassword) {
    const response = await api.post('/auth/change-password', {
      currentPassword,
      newPassword,
    });

    if (response.success) {
      return true;
    }

    throw new Error(response.message || 'Failed to change password');
  }

  /**
   * Upload avatar
   * @param {FormData} formData - Form data with avatar file
   * @returns {Promise<string>} Avatar URL
   */
  async uploadAvatar(formData) {
    const response = await api.post('/auth/avatar', formData);

    if (response.success && response.data) {
      // Update stored user with new avatar
      const currentUser = api.getUser();
      if (currentUser) {
        currentUser.avatar = response.data.avatar || response.data.user?.avatar;
        api.setUser(currentUser);
      }
      return response.data.avatar || response.data.user?.avatar;
    }

    throw new Error('Failed to upload avatar');
  }
}

const authService = new AuthService();
export default authService;
